/**
 * Text-to-Speech (TTS) & Audio Alert Utility for KStore
 * Guaranteed to play cleanly across all browsers, macOS, Windows, iOS, and Android.
 */

let cachedVoices = [];
let speechDebounceTimer = null;
let isAudioUnlocked = false;

// Initialize and cache available voices
const updateVoices = () => {
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    cachedVoices = window.speechSynthesis.getVoices() || [];
  }
};

if (typeof window !== "undefined" && "speechSynthesis" in window) {
  updateVoices();
  window.speechSynthesis.onvoiceschanged = updateVoices;
}

/**
 * Mobile Autoplay Unlocker
 * Mobile browsers (iOS Safari, Android Chrome) block speech synthesis
 * until a user touch gesture primes the audio pipeline.
 */
export const unlockMobileAudio = () => {
  if (typeof window === "undefined" || isAudioUnlocked) return;

  try {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.resume();
      // Speak a silent micro-utterance during a user gesture to permanently unlock mobile WebKit / Chromium
      const silent = new SpeechSynthesisUtterance(" ");
      silent.volume = 0.01;
      silent.rate = 10;
      window.speechSynthesis.speak(silent);
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      const dummyCtx = new AudioContextClass();
      if (dummyCtx.state === "suspended") {
        dummyCtx.resume();
      }
    }

    isAudioUnlocked = true;
    console.log("🔊 Mobile audio/speech unlocked successfully on touch");
  } catch (e) {
    console.warn("Mobile audio unlock notice:", e);
  }
};

// Automatically listen for first user touch/click to unlock mobile audio
if (typeof window !== "undefined") {
  const unlockEvents = ["touchstart", "touchend", "click", "keydown"];
  unlockEvents.forEach((evt) => {
    window.addEventListener(evt, unlockMobileAudio, { once: true, passive: true });
  });
}

/**
 * Checks whether Speech Synthesis is supported in the current environment
 */
export const isSpeechSupported = () => {
  return typeof window !== "undefined" && "speechSynthesis" in window;
};

/**
 * Checks if TTS audio announcements are enabled by the user (persisted in localStorage)
 */
export const isTtsEnabled = () => {
  if (typeof window === "undefined") return false;
  const saved = localStorage.getItem("kstore_tts_enabled");
  return saved === null ? true : saved === "true";
};

/**
 * Toggle TTS enabled state
 */
export const setTtsEnabled = (enabled) => {
  if (typeof window !== "undefined") {
    localStorage.setItem("kstore_tts_enabled", enabled ? "true" : "false");
  }
};

/**
 * Stops any active speech synthesis
 */
export const stopSpeaking = () => {
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    try {
      if (speechDebounceTimer) {
        clearTimeout(speechDebounceTimer);
        speechDebounceTimer = null;
      }
      window.speechSynthesis.cancel();
      window.__kstoreSpeechUtterance = null;
    } catch (e) {
      console.warn("stopSpeaking note:", e);
    }
  }
};

/**
 * Clean text for natural speech synthesis
 */
export const sanitizeForSpeech = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/#(\d+)/g, "number $1")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/[*_~`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Direct, high-reliability Speech Synthesis Player
 * Specially engineered for Android Chrome, iOS Safari, macOS, and Windows.
 */
export const speakDynamicVoice = (text, options = {}) => {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) {
    console.warn("speechSynthesis is not supported on this browser/platform.");
    return false;
  }

  if (!isTtsEnabled() && !options.force) {
    console.log("TTS is disabled by user settings.");
    return false;
  }

  const cleanText = sanitizeForSpeech(text);
  if (!cleanText) return false;

  try {
    const synth = window.speechSynthesis;

    // Clear any previous debounce timer
    if (speechDebounceTimer) {
      clearTimeout(speechDebounceTimer);
      speechDebounceTimer = null;
    }

    // Cancel existing speech and resume if paused
    synth.cancel();
    if (synth.paused) {
      synth.resume();
    }

    // Android Chrome requires ~60ms debounce between cancel() and speak()
    // to prevent the cancellation from dropping the new utterance.
    speechDebounceTimer = setTimeout(() => {
      try {
        if (synth.paused) {
          synth.resume();
        }

        // ALWAYS instantiate a fresh SpeechSynthesisUtterance (never reuse across calls)
        const utterance = new SpeechSynthesisUtterance(cleanText);
        utterance.lang = "en-US";
        utterance.rate = 0.95;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;

        // Pick the best English voice if available
        const voices = cachedVoices.length > 0 ? cachedVoices : synth.getVoices();
        if (voices && voices.length > 0) {
          const preferredVoice =
            voices.find(
              (v) =>
                (v.lang === "en-US" || v.lang.startsWith("en")) &&
                (v.default ||
                  v.name.includes("Google") ||
                  v.name === "Samantha" ||
                  v.name.includes("David") ||
                  v.name.includes("Zira") ||
                  v.name.includes("Microsoft") ||
                  v.localService)
            ) || voices.find((v) => v.lang.startsWith("en"));

          if (preferredVoice) {
            utterance.voice = preferredVoice;
          }
        }

        // Protect from JavaScript Garbage Collection bug in Android Chrome & Safari
        window.__kstoreSpeechUtterance = utterance;

        utterance.onstart = () => {
          console.log("🔊 KStore TTS voice speaking:", cleanText);
        };

        utterance.onend = () => {
          console.log("✅ KStore TTS voice finished speaking");
          window.__kstoreSpeechUtterance = null;
        };

        utterance.onerror = (err) => {
          console.warn("KStore TTS voice event:", err?.error || err);
          window.__kstoreSpeechUtterance = null;
          if (synth.paused) {
            synth.resume();
          }
        };

        synth.speak(utterance);
      } catch (innerErr) {
        console.error("Direct speak execution error:", innerErr);
      }
    }, 60);

    return true;
  } catch (err) {
    console.error("speakDynamicVoice error:", err);
    return false;
  }
};

/**
 * Backward compatibility alias for speakDynamicVoice
 */
export const speakText = speakDynamicVoice;

/**
 * Determines whether an incoming FCM payload is for an order or customer wishlist
 */
export const isOrderPayload = (payload) => {
  if (!payload) return false;

  const dataType = (
    payload?.data?.type ||
    payload?.data?.notification_type ||
    ""
  ).toLowerCase();

  const hasOrderId = Boolean(
    payload?.data?.order_id ||
    payload?.data?.orderId ||
    payload?.data?.kansas_order_id
  );

  const title = (
    payload?.notification?.title ||
    payload?.data?.title ||
    ""
  ).toLowerCase();

  const body = (
    payload?.notification?.body ||
    payload?.data?.body ||
    ""
  ).toLowerCase();

  return (
    dataType === "customer_wishlist" ||
    dataType.includes("wishlist") ||
    dataType === "order" ||
    dataType.includes("order") ||
    dataType === "kansas_order" ||
    hasOrderId ||
    title.includes("order") ||
    body.includes("order") ||
    title.includes("wishlist") ||
    body.includes("wishlist") ||
    title.includes("product") ||
    body.includes("product") ||
    title.includes("customer") ||
    body.includes("customer")
  );
};

/**
 * Speaks the notification using strictly only the received notification text.
 */
export const speakOrderNotification = (bodyText, titleText = "") => {
  let cleanBody = sanitizeForSpeech(bodyText);
  let cleanTitle = sanitizeForSpeech(titleText);

  let messageToSpeak = cleanBody || cleanTitle || "";
  if (!messageToSpeak) return false;

  console.log("TTS Announcing:", messageToSpeak);
  return speakDynamicVoice(messageToSpeak);
};

/**
 * Optional store bell chime (for testing or manual use, never played on top of voice)
 */
export const playNotificationChime = () => {
  try {
    if (typeof window === "undefined") return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    if (ctx.state === "suspended") {
      ctx.resume();
    }

    const now = ctx.currentTime;
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(587.33, now);
    gain1.gain.setValueAtTime(0.2, now);
    gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.35);
  } catch (err) {
    console.warn("Audio chime playback note:", err?.message);
  }
};

// Expose global test helpers on window for console and mobile testing
if (typeof window !== "undefined") {
  const runTestSpeech = (customText) => {
    return speakOrderNotification(
      customText || "Sethalan added 2 product(s) to wishlist"
    );
  };
  window.testKStoreSpeech = runTestSpeech;
  window.textKStoreSpeech = runTestSpeech;
  window.speakOrderNotification = speakOrderNotification;
  window.speakDynamicVoice = speakDynamicVoice;
  window.speakText = speakDynamicVoice;
  window.unlockMobileAudio = unlockMobileAudio;
}
